import React, { useEffect, useState } from 'react';
import { Box, InlineGrid, TextField, Text, Button, Card, Layout, BlockStack } from '@shopify/polaris';
import axios from 'axios';
import { useBarcodeScanner } from './useBarcodeScanner';
import { useToast } from '#client/components/common/Toast/ToastContext';

function AddProductForm({ onSubmit, step, locationId, isHidden }) {
    const [product, setProduct] = useState({ adjustmentAmount: step, sku: '', barcode: '' });
    const [validationErrors, setValidationErrors] = useState({});
    const { addToast } = useToast();

    useEffect(() => {
        setProduct({ ...product, adjustmentAmount: step });
    }, [step]);

    const handleAddAdjustment = async (adjustedProduct) => {
        if (!adjustedProduct.barcode && !adjustedProduct.sku) {
            addToast({
                type: 'error',
                message: 'Please enter either a product barcode or SKU',
            });
            return;
        }
        if (!product.adjustmentAmount) {
            setValidationErrors({ adjustmentAmount: 'Adjustment amount is required' });
            return;
        } else {
            setValidationErrors({ adjustmentAmount: '' });
        }
        const validation = await axios.post(`${import.meta.env.VITE_API_URL}/api/app/inventory-adjustments/product-lookup`, {
            locationId,
            lookup: adjustedProduct.barcode ? `barcode:${adjustedProduct.barcode}` : `sku:${adjustedProduct.sku}`,
        });
        if (validation.data.data.length === 0) {
            addToast({
                type: 'error',
                message: 'Product not found',
            });
            return;
        }

        if (typeof onSubmit === 'function') {
            onSubmit({ ...validation.data.data[0], adjustmentAmount: product.adjustmentAmount });
            setProduct({ adjustmentAmount: step, sku: '', barcode: '' });
        }
    };

    const barcodeScanner = useBarcodeScanner((value) => {
        handleAddAdjustment({ ...product, sku: null, barcode: value });
    });

    // Do not render if a store location has not been selected
    // Store location is required to determine the product inventory
    if (!locationId || isHidden) {
        return null;
    }

    return (
        <div id="add-product-form">
            <Box paddingBlockEnd="200" paddingBlockStart="200">
                <Card>
                    <Layout>
                        <Layout.Section>
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleAddAdjustment(product);
                                }}
                            >
                                <Box paddingBlockEnd="100" paddingBlockStart="100">
                                    <Text variant="headingMd">Inventory Adjustment Item Entry</Text>
                                </Box>
                                <div style={{ margin: '10px' }}>
                                    <BlockStack inlineAlign="end">
                                        <Button
                                            variant="primary"
                                            tone={barcodeScanner.isActive ? 'critical' : 'primary'}
                                            onClick={() => {
                                                if (barcodeScanner.isActive) {
                                                    barcodeScanner.stopScanning();
                                                } else {
                                                    barcodeScanner.startScanning();
                                                }
                                            }}
                                        >
                                            {barcodeScanner.isActive ? 'Stop Scanning' : 'Start Scanning'}
                                        </Button>
                                    </BlockStack>
                                </div>

                                <InlineGrid gap="400" columns={['oneHalf', 'oneHalf']}>
                                    <TextField
                                        value={product.sku}
                                        onChange={(value) => setProduct({ ...product, sku: value, barcode: '' })}
                                        autoComplete="off"
                                        label="Product SKU"
                                        error={validationErrors.sku}
                                    />
                                    <TextField
                                        value={product.barcode}
                                        onChange={(value) => setProduct({ ...product, barcode: value, sku: '' })}
                                        autoComplete="off"
                                        label="Product Barcode"
                                        error={validationErrors.barcode}
                                    />
                                </InlineGrid>
                                <div style={{ margin: '10px' }}>
                                    <TextField
                                        type="number"
                                        align="end"
                                        value={product.adjustmentAmount}
                                        onChange={(value) => {
                                            if (!Number.isNaN(parseInt(value, 10))) {
                                                setProduct({ ...product, adjustmentAmount: parseInt(value, 10) });
                                            } else {
                                                setProduct({ ...product, adjustmentAmount: '' });
                                            }
                                        }}
                                        error={validationErrors.adjustmentAmount ?? ''}
                                        inputMode="numeric"
                                        autoComplete="off"
                                        label="Adjustment Amount"
                                    />
                                </div>
                                <div style={{ margin: '10px' }}>
                                    <BlockStack inlineAlign="end">
                                        <Button submit variant="primary">
                                            Add Item
                                        </Button>
                                    </BlockStack>
                                </div>
                            </form>
                        </Layout.Section>
                    </Layout>
                </Card>
            </Box>
        </div>
    );
}

export default AddProductForm;
